import websiteLeadsImage from "../assets/images/blog/website-leads.jpg";
import seoVsAdsImage from "../assets/images/blog/seo-vs-ads.jpg";
import conversionsImage from "../assets/images/blog/conversions.jpg";
import marketingBudgetImage from "../assets/images/blog/marketing-budget.jpg";
import localSeoImage from "../assets/images/blog/local-seo.jpg";

const blogTopics = [
  {
    slug: "ai-search-homepage-clarity",
    category: "AI Search",
    trendBadge: "Trend watch",
    title: "AI Search Is Raising the Bar for Homepage Clarity",
    excerpt:
      "What now matters most is not clever copy — it is a page that answers the buyer quickly, clearly and with proof.",
    readTime: "7 min",
    author: "Udit Shailesh",
    authorRole: "Strategy Writer",
    publishDate: "2026-06-18",
    featured: true,
    image: websiteLeadsImage,
    imageAlt: "Premium team reviewing website performance and lead flow",
    takeaways: [
      "Use one clear promise at the top of the page.",
      "Put proof, outcomes and FAQs closer to the hero.",
      "Write for humans first, then make the structure machine-friendly.",
    ],
    content: `
AI search is changing how people discover brands. Instead of sending someone through a long journey, search systems try to answer faster and more directly. That means your homepage has to work harder than before.

The pages that perform best now are usually simple to scan. They open with a sharp promise, back it up with numbers or proof, and guide the visitor toward one obvious next step. In other words, clarity is doing the heavy lifting.

For service businesses, this is a good thing. If your proposition is strong, the page becomes easier to understand both for people and for search systems. The goal is no longer to sound bigger — it is to sound clearer, more useful and more trustworthy.
`,
  },
  {
    slug: "whatsapp-follow-up-india",
    category: "Lead Nurture",
    trendBadge: "India first",
    title: "Why WhatsApp Follow-Up Is Winning More Leads Than Slow Email Chains",
    excerpt:
      "Indian buyers respond faster when the conversation feels personal, immediate and easy to continue on mobile.",
    readTime: "6 min",
    author: "Shailesh Verma",
    authorRole: "Growth Editor",
    publishDate: "2026-06-12",
    featured: true,
    image: localSeoImage,
    imageAlt: "Mobile-first communication and local business lead follow-up",
    takeaways: [
      "Respond within minutes, not hours.",
      "Keep the first message short and helpful.",
      "Move from inquiry to scheduling in one thread.",
    ],
    content: `
The fastest sales wins usually happen in the first conversation. In India, that conversation often belongs on WhatsApp because it feels natural, immediate and low-friction for the buyer.

Email still has a place, but it works best after the first touch. When a lead comes in, the winner is usually the team that responds with context, a useful next step and a message that feels written by a person rather than an automation stack.

If your forms are generating traffic but not enough meetings, look at the handoff. Shorter response times, clearer qualification and a better follow-up sequence can change conversion more than another ad campaign.
`,
  },
  {
    slug: "landing-page-proof-formula",
    category: "Conversion Design",
    trendBadge: "UX pattern",
    title: "The 2026 Landing Page Formula: Proof, Clarity and One Next Step",
    excerpt:
      "The best landing pages do fewer things — but they do them with more confidence and much less friction.",
    readTime: "8 min",
    author: "Eklavya Rao",
    authorRole: "Product Writer",
    publishDate: "2026-06-07",
    featured: true,
    image: conversionsImage,
    imageAlt: "Modern landing page analytics and conversion performance",
    takeaways: [
      "Lead with a single outcome, not a feature list.",
      "Show proof before the user has to ask for it.",
      "Keep the CTA visible and the path short.",
    ],
    content: `
Most landing pages lose momentum because they try to explain everything at once. The higher-converting pages are more disciplined: one promise, one audience, one action.

Proof is the real conversion lever. That can be a testimonial, a metric, a client logo or a short explanation of the process. The important part is that the visitor does not have to hunt for reassurance.

In practice, the page should feel like a calm conversation. Lead with the outcome, follow with evidence and remove anything that distracts from the next step.
`,
  },
  {
    slug: "first-party-data-growth",
    category: "Growth Ops",
    trendBadge: "System thinking",
    title: "First-Party Data Is Becoming the Real Media Budget",
    excerpt:
      "The brands that keep learning from their own leads, sales calls and repeat customers will spend less to grow more.",
    readTime: "7 min",
    author: "Udit Shailesh",
    authorRole: "Growth Strategist",
    publishDate: "2026-05-29",
    featured: false,
    image: marketingBudgetImage,
    imageAlt: "Growth planning and budget analytics on a premium desk",
    takeaways: [
      "Capture better data at the form level.",
      "Feed sales feedback back into marketing.",
      "Use the CRM as a learning system, not a storage box.",
    ],
    content: `
Paid channels get expensive when the team keeps repeating the same guesswork. First-party data helps lower that cost because it shows what people actually want, where they drop off and which leads are worth more.

The best teams treat every lead, call and repeat purchase as a signal. They use that signal to improve targeting, content, pricing and the questions they ask on forms.

This is why growth systems matter more than isolated campaigns. When marketing, sales and reporting are connected, your own data becomes the strongest media asset you have.
`,
  },
  {
    slug: "local-seo-still-compounds",
    category: "Local SEO",
    trendBadge: "Evergreen",
    title: "Local SEO Still Compounds When the Buying Intent Is High",
    excerpt:
      "For service businesses, visibility close to the point of need still produces some of the cleanest leads.",
    readTime: "6 min",
    author: "Shailesh Kumar",
    authorRole: "Editorial Lead",
    publishDate: "2026-05-21",
    featured: false,
    image: seoVsAdsImage,
    imageAlt: "Search results and local discovery strategy for a business",
    takeaways: [
      "Refresh location pages with real service detail.",
      "Use reviews and FAQs to improve trust.",
      "Keep the business profile active and accurate.",
    ],
    content: `
Local SEO does not disappear when trends change. It just gets more competitive. The businesses that keep winning are usually the ones that update their location pages, maintain their Google Business Profile and answer the questions buyers actually ask.

The win is not only in rankings. It is in trust. When someone sees a consistent story across search, maps, reviews and the website, the business feels easier to choose.

That is why local SEO remains one of the highest-intent channels for service brands. It captures people at the moment they are already ready to act.
`,
  },
];

export default blogTopics;
